<template>
  <v-dialog v-model="dialog" fullscreen transition="dialog-bottom-transition">
    <v-container class="py-0 fill-height" fluid>
      <v-row class="fill-height">
        <v-col cols="12" class="px-0 py-0">
          <v-card height="100%">
            <v-toolbar
              color="indigo darken-2"
              dense
              dark>
              <v-toolbar-title>
                <v-icon left>{{ demoVideoIcon }}</v-icon>

                {{ $vuetify.lang.t('$vuetify.map.demoVideo.title') }}

              </v-toolbar-title>

              <v-spacer></v-spacer>

              <v-btn icon class="text-right" @click="dialogCloseRequest">
                <v-icon>{{ closeIcon }}</v-icon>
              </v-btn>

              <v-progress-linear :active="isLoading" indeterminate absolute bottom>
              </v-progress-linear>
            </v-toolbar>

            <v-card-text>
              <v-container fluid class="py-0">
                <v-row dense align-content="center">
                  <v-col cols="12" md="6">
                    <datetime-chart-filter :start-date="`${filters.date.start.date}`"
                                           :finish-date="`${filters.date.finish.date}`"
                                           :trim-date="false"
                                           :header="$vuetify.lang.t('$vuetify.charts.filters.date.title')"
                                           @datetime-changed="setDatetime"></datetime-chart-filter>
                  </v-col>
                  <v-col class="py-0" cols="12" md="6">
                    <v-row dense>
                      <v-col md="6" cols="12">
                        <video-type-selector @video-type-changed="videoTypeChanged"></video-type-selector>
                      </v-col>
                      <v-col md="6" cols="12">
                        <state-selector @state-changed="stateChanged"></state-selector>
                      </v-col>
                    </v-row>
                    <v-row dense>
                      <v-col>
                        <drivers-search-autocomplete @driver-choice="setDriver" :nano-users="true"
                                                     :multiply-choice="true"></drivers-search-autocomplete>
                      </v-col>
                    </v-row>
                  </v-col>
                </v-row>
                <v-card-actions>
                  <v-spacer></v-spacer>
                  <v-btn color="success" tile @click="updateFilters" class="mx-n2">
                    <v-icon left>{{ applyFiltersIcon }}</v-icon>
                    {{ $vuetify.lang.t('$vuetify.shared.apply') }}
                  </v-btn>
                </v-card-actions>

                <v-data-iterator
                  :items="videos"
                  :page.sync="page"
                  :items-per-page.sync="pageNumber"
                  :footer-props="footerProps"
                >
                  <template v-slot:default="props">
                    <v-row class="py-0" no-gutters justify="center">
                      <v-col cols="12" md="4" v-for="entry in props.items" :key="entry.datetime">
                        <v-card flat>
                          <v-card-title class="d-flex text-center justify-center">
                            <v-btn icon :href="`${siteUrl}?travel=${entry.travelId}&critical=${entry.event.idCriticalEvent}`" target="_blank">
                              <v-icon>{{ icons[entry.event.dangerousStateNormalized] }}</v-icon>
                            </v-btn>
                        <span> {{ entry.event.dangerousStateLocalized }} </span>
                          </v-card-title>
                          <v-card-subtitle class="text-center py-0">{{ entry.event.formattedTimeEventStart }}
                          </v-card-subtitle>

                          <critical-video :event="entry.event" :user-id="entry.user" :key="`video-${entry.datetime}`"></critical-video>
                        </v-card>
                      </v-col>
                    </v-row>
                  </template>
                </v-data-iterator>
              </v-container>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </v-dialog>
</template>

<script>
import backend from '../../../backend'
import { mdiClose, mdiFilterOutline, mdiVideo } from '@mdi/js'
import DatetimeChartFilter from '../../charts/utils/datetimeChartFilter'
import { defaultChartDatetimeState } from '../../../utils/filters'
import DriversSearchAutocomplete from '../../charts/DriversSearchAutocomplete'
import CriticalVideo from '../criticalPopup/criticalVideo'
import { CriticalEvent } from '../../../models/map/CriticalEvent'
import { dangerousStateIcons } from '../../../utils/icons'
import StateSelector from './stateSelector'
import VideoTypeSelector from './videoTypeSelector'
import { CriticalVideoType } from '../../../enum'

const getDefaultFilters = () => ({
  date: {
    start: defaultChartDatetimeState(''),
    finish: defaultChartDatetimeState('')
  },
  drivers: [],
  states: [],
  video: CriticalVideoType.DEMO
})

export default {
  name: 'demoVideosDialog',
  components: { VideoTypeSelector, StateSelector, CriticalVideo, DriversSearchAutocomplete, DatetimeChartFilter },
  props: {
    dialog: Boolean
  },
  data: () => ({
    filters: getDefaultFilters(),
    isLoading: false,

    videos: [],
    page: 1,
    pageNumber: 12,
    footerProps: { 'items-per-page-options': [1, 6, 12, 18, 24, 36] },

    icons: dangerousStateIcons(),

    demoVideoIcon: mdiVideo,
    closeIcon: mdiClose,
    applyFiltersIcon: mdiFilterOutline
  }),
  computed: {
    numberOfPages () {
      return Math.ceil(this.videos.length / this.itemsPerPage)
    },
    siteUrl () {
      return document.location.origin + document.location.pathname
    }
  },
  methods: {
    async loadDemoVideos () {
      this.isLoading = true
      try {
        const response = await backend.loadDemoVideos({ ...this.filters })
        this.videos = response.data.map(entry => ({ event: new CriticalEvent({ ...entry }), user: entry.userid, travelId: entry.travel_id })) // response.data
      } catch (e) {
        console.error(e)
      } finally {
        this.isLoading = false
      }
    },
    setDatetime (datetime) {
      this.filters.date[datetime.type][datetime.mode] = datetime.value
    },
    setDriver (driver) {
      this.filters.drivers = driver
    },
    stateChanged (states) {
      this.filters.states = states
    },
    videoTypeChanged (videoType) {
      this.filters.video = videoType
    },
    async updateFilters () {
      await this.loadDemoVideos()
    },

    nextPage () {
      if (this.page + 1 <= this.numberOfPages) this.page += 1
    },
    formerPage () {
      if (this.page - 1 >= 1) this.page -= 1
    },
    updateItemsPerPage (number) {
      this.itemsPerPage = number
    },

    dialogCloseRequest () {
      this.$emit('close-demo-video-dialog')
      this.videos = []
    }
  },
  watch: {
    dialog: {
      async handler (value) {
        if (value) {
          await this.loadDemoVideos()
        }
      }
    }
  }
  // async created () {
  //   await this.loadDemoVideos()
  // }
}
</script>

<style scoped>

</style>
